// The site's own visit count (2 Nov 2026), as greeksoup.ai keeps it (account.js there). Two small notes to the worker
// (cloudflare/visits.mjs): a page was opened (its path, the site that linked here, a campaign tag, its number in this
// visit) and, on leaving it, how long it was on screen. The visit's id lives in this tab only (sessionStorage); the
// worker keeps no address and sets no cookie. It is what gives the admin dashboard its bounce rate, time on a page and
// entry pages, which Cloudflare's sampled count cannot. This site is one app, so a page is each change of address,
// not each load. Never from an admin's browser (marked once the admin page opens for an admin), nor one a script drives.
// A third note, once a visit, says a person's hand showed (mouse, finger, key, wheel): scrapers load pages in a real
// Chrome but never touch them, so only touched visits count as readers (2 Nov 2026).
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const ME = "sn-me", VISIT = "sn-v";
let open: { p: string; n: number; s: string; shown: number; ms: number } | null = null;
let hooked = false, touched = "";

const off = () => { try { return localStorage.getItem(ME) === "1" || navigator.webdriver; } catch { return true; } };
const send = (o: object) => { try { navigator.sendBeacon("/api/data/e", new Blob([JSON.stringify(o)], { type: "text/plain" })); } catch { /* not sent */ } };

// the first real input of a visit, sent once; the visit's id in storage keeps it once across its pages
function hand(e: Event) {
  if (!open || !e.isTrusted || touched === open.s) return;
  touched = open.s;
  try {
    const v = JSON.parse(sessionStorage.getItem(VISIT) || "null");
    if (v && v.h) return;
    if (v) { v.h = 1; sessionStorage.setItem(VISIT, JSON.stringify(v)); }
  } catch { /* storage refused: sent once for this page */ }
  send({ t: "h", p: open.p, n: open.n, s: open.s });
}

export const markAdminBrowser = () => { try { localStorage.setItem(ME, "1"); } catch { /* storage refused */ } };

function leave() {
  if (!open) return;
  if (open.shown) { open.ms += Date.now() - open.shown; open.shown = 0; }
  send({ t: "l", p: open.p, n: open.n, s: open.s, ms: open.ms });
  open = null;
}

function view(path: string, first: boolean) {
  leave();
  let v: { s: string; n: number } | null = null;
  try { v = JSON.parse(sessionStorage.getItem(VISIT) || "null"); } catch { v = null; }
  if (!v || !v.s) v = { s: (Date.now().toString(36) + Math.random().toString(36).slice(2, 10)).slice(0, 20), n: 0 };
  v.n++;
  try { sessionStorage.setItem(VISIT, JSON.stringify(v)); } catch { /* the visit is this page only */ }
  // the referring site only for the address the tab arrived on; a move inside the app has none
  let ref = "";
  if (first) { try { ref = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : ""; } catch { ref = ""; } }
  if (/^shikshannivesh\.com$/.test(ref)) ref = "";
  const p = ("/" + path.replace(/[^a-z0-9._/-]/gi, "").replace(/^\/+/, "")).slice(0, 200);
  const utm = first ? new URLSearchParams(location.search).get("utm_source") || "" : "";
  open = { p, n: v.n, s: v.s, shown: document.visibilityState === "visible" ? Date.now() : 0, ms: 0 };
  send({ t: "v", p, r: ref, u: utm, n: v.n, s: v.s });
}

// mounted once inside the router: a note for every address the reader moves to
export function VisitCount() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (off() || pathname.startsWith("/admin")) { leave(); return; }
    if (!hooked) {
      hooked = true;
      document.addEventListener("visibilitychange", () => {
        if (!open) return;
        if (document.visibilityState === "visible") { open.shown = Date.now(); return; }
        if (open.shown) { open.ms += Date.now() - open.shown; open.shown = 0; }
      });
      window.addEventListener("pagehide", leave);
      ["pointermove", "pointerdown", "touchstart", "keydown", "wheel"].forEach((t) => window.addEventListener(t, hand, { passive: true, capture: true }));
      view(pathname, true);
      return;
    }
    view(pathname, false);
  }, [pathname]);
  return null;
}
